import { IPurchaseInfo } from './../../../types/IPurchaseInfo';
import { IMeCollection } from './../../../types/ICollection';
import { ICollection } from './../../../types/ICollection';
import axios, { AxiosRequestConfig } from 'axios';

import { get } from '~/App/helpers/cookie';

import { mwAxios, siteAxios } from '.';
import { IMePurchase } from '~/types/IPurchase';
import { ICommerceOrderLine } from '~/types/ICommerceOrderLine';

type Arguments = AxiosRequestConfig & {
  secretKey?: string;
};

type ArgumentsWithSlug = AxiosRequestConfig & {
  slug: string;
  secretKey?: string;
};

const fetch = (args: Arguments) => {
  const request: Arguments = {
    ...{ method: 'get' },
    ...args
  };
  return axios(request);
};

const supporters = {
  index: (args: Arguments) => {
    return siteAxios({
      url: `/api/v2/mw/supporters`,
      method: 'get',
      ...args
    });
  }
};

type PaymentMethodCategory = {
  identifier: string;
  name: string;
};

export type SessionResponse = {
  id: string;
  client_token: string;
  payment_method_categories: PaymentMethodCategory[];
};

export type SessionDetailsResponse = {
  billing_address?: {
    city?: string;
    country?: string;
    email?: string;
    family_name?: string;
    given_name?: string;
    phone?: string;
    postal_code?: string;
    street_address?: string;
    street_address2?: string;
  };
};

export type KlarnaCreateSessionArguments = {
  data: {
    totalPrice: number;
    shippingPrice: number;
    redirectUrl: string;
    orderLines: ICommerceOrderLine[];
  };
};

export type KlarnaUpdateSessionArguments = {
  id: string;
  price: number;
};

const klarna = {
  createSession: async ({ data, ...args }: Omit<Arguments, 'data'> & KlarnaCreateSessionArguments) => {
    const response = await mwAxios.post<SessionResponse>(
      `/api/v2/klarna/sessions`,
      data,
      {
        headers: {
          'X-Session-Key': args.secretKey || get('secretKey')
        }
      }
    );

    return response.data;
  },
  updateSession: async ({ id, price, ...args }: Arguments & KlarnaUpdateSessionArguments) => {
    const response = await mwAxios.put<SessionResponse>(
      `/api/v2/klarna/sessions/${id}`,
      { price },
      {
        headers: {
          'X-Session-Key': args.secretKey || get('secretKey')
        }
      }
    );

    return response.data;
  },
  getSession: async (id: string) => {
    const response = await mwAxios.get<SessionDetailsResponse>(
      `/api/v2/klarna/sessions/${id}`
    );

    return response.data;
  }
};

const meCollections = {
  index: (args: Arguments) => {
    return siteAxios.get<IMeCollection[]>(`/api/v2/mw/users/me/collections`, {
      headers: {
        'X-Session-Key': args.secretKey || get('secretKey')
      },
      params: {
        ...args.params
      }
    });
  },
  show: (args: ArgumentsWithSlug) => {
    return siteAxios.get<IMeCollection>(
      `/api/v2/mw/users/me/collections/${args.slug}`,
      {
        headers: {
          'X-Session-Key': args.secretKey || get('secretKey')
        },
        params: {
          ...args.params
        }
      }
    );
  },
  unpause: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}/unpause`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      }
    }),
  enabled: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}/enable`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      }
    })
};

const collections = {
  index: <T extends ICollection = ICollection>(args: Arguments) =>
    siteAxios.get<T[]>(`/api/v2/mw/collections`, {
      params: {
        ...args.params
      }
    }),
  show: <T extends ICollection = ICollection>(args: ArgumentsWithSlug) =>
    siteAxios.get<T>(`/api/v2/mw/collections/${args.slug}`),
  owners: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}/owners`,
      method: 'get',
      params: {
        ...args.params
      }
    }),
  deleteOwner: (args: ArgumentsWithSlug) =>
    siteAxios.delete(`/api/v2/mw/collections/${args.slug}/owners`, {
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    }),
  edit: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}`,
      method: 'put',
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    }),
  pause: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}/pause`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    }),
  disabled: (args: ArgumentsWithSlug) =>
    siteAxios({
      url: `/api/v2/mw/collections/${args.slug}/disable`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    }),
  create: (args: Arguments) =>
    siteAxios({
      url: `/api/v2/mw/collections`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    }),
  ownerJoinCode: (args: Arguments) =>
    siteAxios({
      ...args,
      url: `/api/v2/mw/collections/owners`,
      method: 'post',
      headers: {
        'X-Session-Key': get('secretKey')
      },
      data: {
        ...args.data
      }
    })
};

const mePurchases = {
  index: async (args: Arguments) => {
    const { data } = await siteAxios.get<IMePurchase[]>(
      `/api/v2/mw/users/me/purchases`,
      {
        headers: {
          'X-Session-Key': args.secretKey || get('secretKey')
        },
        params: {
          ...args.params
        }
      }
    );

    return data;
  },
  purchasesInfo: async ({ secretKey }: Arguments) => {
    const { data } = await siteAxios.get<IPurchaseInfo>(
      `/api/v2/mw/users/me/purchases/info`,
      {
        headers: {
          'X-Session-Key': secretKey || get('secretKey')
        }
      }
    );

    return data;
  }
};

const supportMessages = {
  create: (args: Arguments) =>
    siteAxios({
      url: `/api/v2/mw/support_messages`,
      method: 'post',
      data: {
        ...args.data
      }
    })
};

const emailSubscriptions = {
  create: (args: Arguments) =>
    siteAxios({
      url: `/api/v2/mw/email/subscriptions`,
      method: 'post',
      data: {
        ...args.data
      }
    })
};

const feedback = {
  send: (args: Arguments) =>
    siteAxios({
      url: `/api/v2/mw/feedback`,
      method: 'post',
      data: {
        ...args.data
      }
    }),

  edit: (args: Arguments) => {
    return siteAxios({
      url: `/api/v2/mw/feedback`,
      method: 'put',
      data: {
        ...args.data
      }
    });
  }
};

export {
  fetch,
  supporters,
  meCollections,
  collections,
  mePurchases,
  supportMessages,
  emailSubscriptions,
  klarna,
  feedback
};
